import { useState, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Text,
  Group,
  Badge,
  Button,
  Grid,
  Modal,
  Textarea,
  Stack,
  Alert,
  Radio,
  ActionIcon,
  Menu,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IconAlertCircle,
  IconLock,
  IconLockOpen,
  IconToggleLeft,
  IconToggleRight,
  IconSettings,
  IconTrash,
  IconAdjustments,
} from '@tabler/icons-react';
import { servicesApi, serviceAccessApi, environmentsApi, deploymentsApi } from '../lib/api';
import type { Environment } from '../types';
import { showSuccess, showError } from '../lib/notifications';
import { PageHeader } from '../components/PageHeader';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { RuntimeConfigModal, RuntimeConfigTarget } from '../components/RuntimeConfigModal';
import { useAuth } from '../contexts/AuthContext';

export function ServiceDetailsPage() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [selectedEnv, setSelectedEnv] = useState<Environment | null>(null);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [runtimeConfigTarget, setRuntimeConfigTarget] = useState<RuntimeConfigTarget | null>(null);
  const [pendingAction, setPendingAction] = useState<{
    type: 'disable' | 'enable' | 'delete';
    envId: string;
    envName: string;
  } | null>(null);


  // Request access mutation
  const accessForm = useForm({
    initialValues: {
      accessType: 'READ' as 'READ' | 'WRITE',
      reason: '',
    },
  });

  const requestAccess = useMutation({
    mutationFn: async (values: { accessType: 'READ' | 'WRITE'; reason?: string }) => {
      if (!selectedEnv) return;
      return serviceAccessApi.requestAccess(selectedEnv.id, values.accessType, values.reason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['service', serviceId]);
      setRequestModalOpen(false);
      setSelectedEnv(null);
      showSuccess('Access request sent successfully');
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to request access');
    },
  });

  // Toggle environment disabled state
  const toggleDisabled = useMutation({
    mutationFn: async ({ envId, disabled }: { envId: string; disabled: boolean }) => {
      return environmentsApi.toggleDisabled(serviceId!, envId, disabled);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['service', serviceId]);
      showSuccess('Environment status updated successfully');
      setDisableConfirmOpen(false);
      setPendingAction(null);
    },
    onError: (error: any) => {
      showError(error.response?.data?.error || 'Failed to update environment status');
    },
  });

  const canManageEnvironments = ['SUPERUSER', 'ADMIN', 'DEVOPS'].includes(user?.role || '');

  // Handler functions for confirmation actions
  const handleDisableToggle = (env: Environment) => {
    setPendingAction({
      type: env.disabled ? 'enable' : 'disable',
      envId: env.id,
      envName: env.name,
    });
    setDisableConfirmOpen(true);
  };

  const handleDeleteService = (env: Environment) => {
    setPendingAction({
      type: 'delete',
      envId: env.id,
      envName: env.name,
    });
    setDeleteConfirmOpen(true);
  };

  const confirmToggleDisabled = () => {
    if (!pendingAction) return;
    toggleDisabled.mutate({
      envId: pendingAction.envId,
      disabled: pendingAction.type === 'disable',
    });
  };

  const confirmDeleteService = () => {
    if (!pendingAction) return;
    deleteCloudRunService.mutate({
      envId: pendingAction.envId,
    });
  };

  // Delete Cloud Run service mutation
  const deleteCloudRunService = useMutation({
    mutationFn: async ({ envId }: { envId: string }) => {
      return deploymentsApi.deleteCloudRunService(serviceId!, envId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['service', serviceId]);
      showSuccess('Cloud Run service deleted successfully');
      setDeleteConfirmOpen(false);
      setPendingAction(null);
    },
    onError: (error: any) => {
      showError(error.response?.data?.error || 'Failed to delete Cloud Run service');
    },
  });

  const { data: service, isLoading: isLoadingService } = useQuery({
    queryKey: ['service', serviceId],
    queryFn: async () => {
      const response = await servicesApi.get(serviceId!);
      return response.data;
    },
    enabled: !!serviceId,
  });


  if (isLoadingService) {
    return <LoadingOverlay message="Loading..." />;
  }

  if (!service) {
    return <div>Service not found</div>;
  }

  return (
    <Fragment>
      <PageHeader
        title={service.name}
        description={service.description || 'No description'}
        breadcrumbs={[
          { label: 'Services', to: '/services' },
          { label: service.name },
        ]}
      >
        {canManageEnvironments && (
          <Button
            variant="light"
            leftIcon={<IconAdjustments size={16} />}
            onClick={() => setRuntimeConfigTarget({ scope: 'defaults', serviceId: serviceId! })}
          >
            Runtime Settings
          </Button>
        )}
      </PageHeader>

      <Grid>
        {service.environments?.map((env: Environment) => (
          <Grid.Col key={env.id} xs={12} sm={6} lg={4}>
            <Card
              shadow="sm"
              p="lg"
              radius="md"
              withBorder
              sx={(theme) => {
                if (!env.disabled) return {};
                const dark = theme.colorScheme === 'dark';
                return {
                  backgroundColor: dark ? '#1c1c1c' : '#f0f0f0',
                  borderColor: dark ? '#2c2c2c' : '#d9d9d9',
                  opacity: 0.6,
                };
              }}
            >
              <Group position="apart" mb="xs">
                <Group spacing={8}>
                  <Text weight={500}>{env.name}</Text>
                  {env.disabled && (
                    <Badge color="red" variant="filled" size="sm">
                      Disabled
                    </Badge>
                  )}
                </Group>
                <Group spacing={8}>
                  {canManageEnvironments && (
                    <Menu shadow="md" zIndex={1000} width={200}>
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray">
                          <IconSettings size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          icon={<IconAdjustments size={14} />}
                          onClick={() =>
                            setRuntimeConfigTarget({
                              scope: 'override',
                              serviceId: serviceId!,
                              envId: env.id,
                              envName: env.name,
                            })
                          }
                        >
                          Runtime Settings
                        </Menu.Item>
                        <Menu.Item
                          icon={env.disabled ? <IconToggleRight size={14} /> : <IconToggleLeft size={14} />}
                          onClick={() => handleDisableToggle(env)}
                        >
                          {env.disabled ? 'Enable Environment' : 'Disable Environment'}
                        </Menu.Item>
                        <Menu.Item
                          icon={<IconTrash size={14} />}
                          color="red"
                          onClick={() => handleDeleteService(env)}
                          disabled={deleteCloudRunService.isLoading}
                        >
                          Delete service
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Group>
              </Group>

              <Group spacing={8} mb="md">
                {env.hasAccess ?? false ? (
                  <Badge color="green" leftSection={<IconLockOpen size={14} />}>
                    {env.accessType === 'WRITE' ? 'Write Access' : 'Read Access'}
                  </Badge>
                ) : (
                  <Badge color="red" leftSection={<IconLock size={14} />}>
                    No Access
                  </Badge>
                )}
                <Badge color="green" variant="light">
                  {env._count.variables} var(s)
                </Badge>
              </Group>

              <Text size="sm" color="dimmed" lineClamp={2} mb="md">
                {env.description || 'No description'}
              </Text>

              <Group position="apart">
                {env.hasAccess ?? false ? (
                  <Button
                    variant="light"
                    onClick={() => navigate(`/services/${serviceId}/environments/${env.id}`)}
                    disabled={env.disabled}
                    title={env.disabled ? "Environment is disabled - variable access is restricted" : "View environment variables"}
                  >
                    View Environment
                  </Button>
                ) : (
                  <Button
                    variant="light"
                    color="thanos"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedEnv(env);
                      setRequestModalOpen(true);
                    }}
                    loading={requestAccess.isLoading && selectedEnv?.id === env.id}
                    disabled={env.disabled}
                    title={env.disabled ? "Environment is disabled - access requests are not allowed" : "Request access to this environment"}
                  >
                    Request Access
                  </Button>
                )}
              </Group>
            </Card>
          </Grid.Col>
        ))}
      </Grid>


      <Modal
        opened={requestModalOpen}
        onClose={() => {
          setRequestModalOpen(false);
          setSelectedEnv(null);
          setError('');
        }}
        title="Request Environment Access"
      >
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            mb="md"
            title="Error"
          >
            {error}
          </Alert>
        )}

        <form onSubmit={accessForm.onSubmit((values) => requestAccess.mutate(values))}>
          <Stack>
            <Text>
              You are requesting access to the <strong>{selectedEnv?.name}</strong> environment
              of service <strong>{service.name}</strong>.
            </Text>

            <Radio.Group
              label="Access Type"
              description="Select the type of access you need"
              {...accessForm.getInputProps('accessType')}
            >
              <Stack mt="xs">
                <Radio value="READ" label="Read Access" description="View environment variables" />
                <Radio value="WRITE" label="Write Access" description="Create, edit, and delete environment variables" />
              </Stack>
            </Radio.Group>

            <Textarea
              label="Reason"
              description="Explain why you need this access"
              placeholder="I need this access to..."
              {...accessForm.getInputProps('reason')}
            />

            <Text size="sm" color="dimmed">
              An admin will review your request and grant you appropriate access.
            </Text>

            <Button
              type="submit"
              loading={requestAccess.isLoading}
              fullWidth
            >
              Submit Request
            </Button>
          </Stack>
        </form>
      </Modal>

      {/* Disable/Enable Environment Confirmation Modal */}
      <ConfirmationModal
        opened={disableConfirmOpen}
        onClose={() => {
          setDisableConfirmOpen(false);
          setPendingAction(null);
        }}
        onConfirm={confirmToggleDisabled}
        title={pendingAction?.type === 'disable' ? 'Disable Environment' : 'Enable Environment'}
        message={
          pendingAction?.type === 'disable'
            ? `Are you sure you want to disable the "${pendingAction?.envName}" environment? This will prevent deployments and restrict variable access.`
            : `Are you sure you want to enable the "${pendingAction?.envName}" environment? This will allow deployments and variable access.`
        }
        confirmLabel={pendingAction?.type === 'disable' ? 'Disable Environment' : 'Enable Environment'}
        isLoading={toggleDisabled.isLoading}
      />

      {/* Delete Cloud Run Service Confirmation Modal */}
      <ConfirmationModal
        opened={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setPendingAction(null);
        }}
        onConfirm={confirmDeleteService}
        title="Delete Cloud Run Service"
        message={`Are you sure you want to delete the Cloud Run service for the "${pendingAction?.envName}" environment? This action will permanently remove the deployed service and cannot be undone.`}
        confirmLabel="Delete Service"
        isLoading={deleteCloudRunService.isLoading}
      />

      <RuntimeConfigModal
        target={runtimeConfigTarget}
        onClose={() => setRuntimeConfigTarget(null)}
      />
    </Fragment>
  );
}
